/**
 * A vendor timestamp as the local time at the place it is for, so a departure
 * reads the same to everyone and the cards and the day strip agree on its day.
 *
 * Three kinds of timestamp arrive: with the local offset of the place (Cargo
 * One air: `10:00-04:00`), where that clock is already the local time; in UTC,
 * where the zone of the place (from the route) is applied when known;
 * and without any offset, which is taken as the wall clock as written. The
 * viewer's own zone is never used. With nothing to go on, a UTC time is shown
 * as UTC.
 */
import dayjs from 'dayjs';

import type { NormalizedQuote } from '../../../apis/lanePlanner';
import { airportZoneOf } from './flightTime';

export interface LocalTime {
  /** The calendar day there, YYYY-MM-DD. */
  day: string;
  /** "10:30 AM". */
  time: string;
  /** "Wed, 10/28/26". */
  date: string;
  /** "EDT", "GMT+2", "UTC", or "" when the zone is unknown. */
  zone: string;
}

const STAMP =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;

const clock = (hours: number, minutes: number): string =>
  `${hours % 12 === 0 ? 12 : hours % 12}:${String(minutes).padStart(2, '0')} ${
    hours < 12 ? 'AM' : 'PM'
  }`;

const formatters = new Map<string, Intl.DateTimeFormat>();

/** One formatter per zone and kind: building one is the costly part of showing a time. */
const formatterFor = (
  timeZone: string,
  kind: 'name' | 'clock'
): Intl.DateTimeFormat => {
  const key = `${kind}:${timeZone}`;
  let format = formatters.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(
      'en-US',
      kind === 'name'
        ? { timeZone, timeZoneName: 'short' }
        : {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }
    );
    formatters.set(key, format);
  }
  return format;
};

const zoneName = (instant: Date, timeZone: string): string => {
  try {
    return (
      formatterFor(timeZone, 'name')
        .formatToParts(instant)
        .find((part) => part.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
};

const DAY_MS = 86400000;

/** The wall clock `timeZone` shows at `instant`, as if that clock were UTC (milliseconds). */
const wallClockMs = (instant: number, timeZone: string): number => {
  const parts = formatterFor(timeZone, 'clock').formatToParts(
    new Date(instant)
  );
  const get = (type: string): number =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  return Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute')
  );
};

/** The instant a wall clock written without an offset means in `timeZone` (a time skipped by a clock change reads as UTC). */
const wallClockInstant = (
  day: string,
  hh: string,
  mm: string,
  timeZone: string
): number => {
  const wall = Date.parse(`${day}T${hh}:${mm}:00Z`);
  for (const nearby of [wall - DAY_MS, wall + DAY_MS]) {
    const instant = wall - (wallClockMs(nearby, timeZone) - nearby);
    if (wallClockMs(instant, timeZone) === wall) return instant;
  }
  return wall;
};

/**
 * "EDT" or "EST" for a wall clock written without an offset: the abbreviation
 * in force at that local time, not at the same digits read as UTC (which is up
 * to a day off around a clock change).
 */
const wallClockZoneName = (
  day: string,
  hh: string,
  mm: string,
  timeZone: string
): string => {
  try {
    return zoneName(
      new Date(wallClockInstant(day, hh, mm, timeZone)),
      timeZone
    );
  } catch {
    return '';
  }
};

const offsetZone = (offset: string): string => {
  const [, sign, hh, mm] = /^([+-])(\d{2}):?(\d{2})$/.exec(offset) ?? [];
  if (!sign) return '';
  const hours = Number(hh);
  return `GMT${sign}${hours}${mm === '00' ? '' : `:${mm}`}`;
};

const isUtcOffset = (offset: string | undefined): boolean =>
  offset === 'Z' || /^[+-]00:?00$/.test(offset ?? '');

const shape = (
  day: string,
  hours: number,
  minutes: number,
  zone: string
): LocalTime => ({
  day,
  time: clock(hours, minutes),
  date: dayjs(day).format('ddd, M/D/YY'),
  zone,
});

/** The wall clock in `timeZone` at `instant`, or null when the zone is not known to Intl. */
const inZone = (instant: Date, timeZone: string): LocalTime | null => {
  try {
    const parts = formatterFor(timeZone, 'clock').formatToParts(instant);
    const get = (type: string): string =>
      parts.find((part) => part.type === type)?.value ?? '00';
    return shape(
      `${get('year')}-${get('month')}-${get('day')}`,
      Number(get('hour')),
      Number(get('minute')),
      zoneName(instant, timeZone)
    );
  } catch {
    return null;
  }
};

export const localTimeOf = (
  iso: string | null | undefined,
  timeZone?: string | null
): LocalTime | null => {
  const match = iso ? STAMP.exec(iso.trim()) : null;
  if (!match) return null;
  const [, day, hh, mm, offset] = match;
  if (offset && !isUtcOffset(offset)) {
    // the local clock of the place, as the vendor wrote it
    const instant = new Date(iso as string);
    const zone =
      timeZone && !Number.isNaN(instant.getTime())
        ? zoneName(instant, timeZone) || offsetZone(offset)
        : offsetZone(offset);
    return shape(day, Number(hh), Number(mm), zone);
  }
  if (!offset) {
    // no offset: the wall clock as written
    const zone = timeZone ? wallClockZoneName(day, hh, mm, timeZone) : '';
    return shape(day, Number(hh), Number(mm), zone);
  }
  const instant = new Date(`${day}T${hh}:${mm}:00Z`);
  const there = timeZone ? inZone(instant, timeZone) : null;
  return there ?? shape(day, Number(hh), Number(mm), 'UTC');
};

/** The quote's first departure, in the local time of its origin. */
export const departureLocalOf = (quote: NormalizedQuote): LocalTime | null => {
  const index = quote.legs.findIndex((leg) => leg.departs_at);
  if (index < 0) return null;
  return localTimeOf(
    quote.legs[index].departs_at,
    index === 0 ? quote.route?.origin_timezone : null
  );
};

/** The zone a leg's stamp is written in: the route's origin or destination at the ends, else none. */
const legZoneOf = (
  quote: NormalizedQuote,
  index: number,
  which: 'departs' | 'arrives'
): string | null | undefined =>
  which === 'departs' && index === 0
    ? quote.route?.origin_timezone
    : which === 'arrives' && index === quote.legs.length - 1
      ? quote.route?.destination_timezone
      : null;

/**
 * A leg's departure or arrival: the first leg leaves from the route's origin
 * and the last arrives at its destination, so those use the route's zones; a
 * connection uses the offset the vendor wrote, or UTC when it wrote none.
 */
export const legLocalTimeOf = (
  quote: NormalizedQuote,
  index: number,
  which: 'departs' | 'arrives'
): LocalTime | null => {
  const leg = quote.legs[index];
  const iso = which === 'departs' ? leg?.departs_at : leg?.arrives_at;
  return localTimeOf(iso, legZoneOf(quote, index, which));
};

/**
 * The moment a leg's departure or arrival stamp means, read as the displayed
 * time is. Null when it cannot be told: a wall clock written without an
 * offset needs the zone of its place: the route's, else the leg's airport.
 */
export const legInstantOf = (
  quote: NormalizedQuote,
  index: number,
  which: 'departs' | 'arrives'
): number | null => {
  const leg = quote.legs[index];
  const iso = which === 'departs' ? leg?.departs_at : leg?.arrives_at;
  const match = iso ? STAMP.exec(iso.trim()) : null;
  if (!match) return null;
  const [, day, hh, mm, offset] = match;
  if (offset) {
    const colon = /^[+-]\d{4}$/.test(offset)
      ? `${offset.slice(0, 3)}:${offset.slice(3)}`
      : offset;
    const stamp = (iso as string).trim().replace(' ', 'T');
    return Date.parse(stamp.slice(0, stamp.length - offset.length) + colon);
  }
  const zone =
    legZoneOf(quote, index, which) ??
    airportZoneOf(which === 'departs' ? leg.origin : leg.destination);
  if (!zone) return null;
  try {
    return wallClockInstant(day, hh, mm, zone);
  } catch {
    return null;
  }
};

/** "10/28/26 10:00 AM EDT" for a leg's time, or null. */
export const formatLegTime = (
  quote: NormalizedQuote,
  index: number,
  which: 'departs' | 'arrives'
): string | null => {
  const local = legLocalTimeOf(quote, index, which);
  if (!local) return null;
  return `${dayjs(local.day).format('M/D/YY')} ${local.time}${local.zone ? ` ${local.zone}` : ''}`;
};
